import React from 'react';
import { Col, Row, Progress, Typography } from 'antd';
import { TAnimateurInfo } from '../../../models/user/Dashboard';
import { useTranslation } from 'react-i18next';
import { TChapter, TCourse, TEpic, TSubject } from '../../../models/courses/course';
import { TLesson } from '../../../models/courses/lesson';

interface AnimateurContentProps {
    userData: { user: TAnimateurInfo; subjects: Set<number>; courses: Set<number> }[];
    subjects: TSubject[];
    courses: TCourse[];
    chapters: TChapter[];
    epics: TEpic[];
    lessons: TLesson[];
    selectedSubject: number | null;
    selectedCourse: number | null;
    palette: {
        primary: string;
        secondary: string;
        background: string;
        border: string;
        muted: string;
    };
}


const getLessonPath = (
    lessonId: number | null,
    lessons: TLesson[],
    epics: TEpic[],
    chapters: TChapter[],
    courses: TCourse[],
    subjects: TSubject[],
): string | null => {
    if (!lessonId) return null;

    const lesson = lessons.find((l) => l.id === lessonId);
    if (!lesson) return null;

    const epic = epics.find((e) => e.id === lesson.epic_id);
    const chapter = epic ? chapters.find((c) => c.id === epic.chapter_id) : null;
    const course = chapter ? courses.find((c) => c.id === chapter.course_id) : null;
    const subject = course ? subjects.find((s) => s.id === course.subject_id) : null;

    if (!subject || !course || !chapter || !epic || !lesson) return null;

    return `${subject.title} / ${course.title} / ${chapter.name} / ${epic.name} / ${lesson.name}`;
};

const AnimateurContent: React.FC<AnimateurContentProps> = ({
    userData,
    selectedSubject,
    selectedCourse,
    subjects,
    courses,
    chapters,
    epics,
    lessons,
    palette,
}) => {
    const { t } = useTranslation();

    if (userData.length === 0) {
        return <p>{t('text.No_user_selected')}</p>;
    }

    return (
        <>
            <Row gutter={[16, 16]}>
                {userData.map(({ user, subjects: userSubjects, courses: userCourses }) => {
                    // Récupérer le chemin de la dernière leçon
                    const lessonPath = getLessonPath(
                        user.last_lesson_id,
                        lessons,
                        epics,
                        chapters,
                        courses, // Utilise tous les cours
                        subjects // Utilise tous les sujets
                    );

                    return (
                        <Col key={user.user_id} xs={24} sm={12} md={8} lg={6}>
                            <div
                                style={{
                                    height: '100%',
                                    padding: '20px 22px',
                                    borderRadius: 24,
                                    background: '#fff',
                                    border: `1px solid ${palette.border}`,
                                    boxShadow: '0 16px 32px rgba(7,24,38,0.1)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 14,
                                }}
                            >
                                <div>
                                    <Typography.Title
                                        level={4}
                                        style={{ margin: 0, fontWeight: 700 }}
                                    >
                                        {user.firstname} {user.lastname}
                                    </Typography.Title>
                                </div>
                                {selectedSubject && (
                                    <div>
                                        <Typography.Text style={{ color: palette.muted }}>
                                            {t('text.Progression_sujet')} :
                                        </Typography.Text>
                                        <Progress
                                            percent={Math.round(
                                                userSubjects.has(selectedSubject)
                                                    ? user.subject_progress_percentage ?? 0
                                                    : 0
                                            )}
                                            size="small"
                                            status="active"
                                            strokeColor={palette.primary}
                                        />
                                    </div>
                                )}
                                {selectedCourse && (
                                    <div>
                                        <Typography.Text style={{ color: palette.muted }}>
                                            {t('text.Progression_cours')} :
                                        </Typography.Text>
                                        <Progress
                                            percent={Math.round(
                                                userCourses.has(selectedCourse)
                                                    ? user.course_progress_percentage ?? 0
                                                    : 0
                                            )}
                                            size="small"
                                            status="active"
                                            strokeColor={palette.secondary}
                                        />
                                    </div>
                                )}
                                <div
                                    style={{
                                        marginTop: 'auto',
                                        padding: '12px 14px',
                                        borderRadius: 16,
                                        background: 'rgba(245, 249, 255, 0.9)',
                                    }}
                                >
                                    <Typography.Text style={{ color: palette.muted }}>
                                        {t('text.Derniere_leçon')} : {lessonPath ?? t('text.No_lesson')}
                                    </Typography.Text>
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>
        </>
    );
};

export default AnimateurContent;


